import { CheckCircle, Close } from "@mui/icons-material";
import { Modal } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { getBidHistoryArchive } from "../../../Services/ReverseAuction/GetApi";
import Styles from "./RABidHistory.module.css";

function formatDateTime(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} ${hours}:${minutes}`;
}

const STAGE_ORDER = ["IPO", "RA"];

const StageSection = ({ entries, showEntityName }) => (
  <div className={Styles.bidSection}>
    <div className={Styles.priceHistoryWrap}>
      <div
        className={`display_flex_row tableHeading_type2 ${Styles.priceHistorySubHead}`}
      >
        <div style={{ width: "8%" }}>S.No</div>
        <div style={{ width: showEntityName ? "20%" : "26%" }}>
          Price (₹/kWh)
        </div>
        <div style={{ width: showEntityName ? "20%" : "26%" }}>
          Quantity (MW)
        </div>
        <div style={{ width: showEntityName ? "24%" : "32%" }}>Updated At</div>
        {showEntityName && (
          <div style={{ width: "20%" }}>Bidder Entity Name</div>
        )}
        <div style={{ width: "8%" }} />
      </div>

      {entries.length > 0 ? (
        entries.map((entry, i) => {
          // Highlight the final bid, but not when the winner bid is rejected —
          // a rejected winner should not be visually emphasized in the RA stage.
          const isHighlighted =
            entry.isFinal && entry.winnerBidStatus !== "REJECTED";
          return (
            <div
              key={entry.id ?? i}
              className={`display_flex_row tableDetails ${Styles.priceHistoryRow} ${isHighlighted ? Styles.finalRow : ""}`}
              style={isHighlighted ? { fontWeight: "bold" } : {}}
            >
              <div style={{ width: "8%" }}>{i + 1}</div>
              <div
                style={{ width: showEntityName ? "20%" : "26%" }}
                className={Styles.priceCell}
              >
                {entry.bidPrice}
              </div>
              <div
                className={Styles.quantityCell}
                style={{ width: showEntityName ? "20%" : "26%" }}
              >
                {entry.bidQuantityMw}
              </div>
              <div style={{ width: showEntityName ? "24%" : "32%" }}>
                {formatDateTime(entry.createdAt)}
              </div>
              {showEntityName && (
                <div style={{ width: "20%" }}>
                  {entry.bidderEntityName ?? "-"}
                </div>
              )}
              <div
                style={{
                  width: "8%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isHighlighted && (
                  <CheckCircle
                    style={{ fontSize: 14, color: "var(--primary-color-text)" }}
                  />
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className={`display_flex_row_center ${Styles.noHistory}`}>
          No price revisions
        </div>
      )}
    </div>
  </div>
);

const RABidHistory = ({
  open,
  handleClose,
  tenderId,
  requisitionId,
  showEntityName = false,
  role,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeStage, setActiveStage] = useState(null);

  useEffect(() => {
    if (!open || !tenderId || !requisitionId) return;
    let mounted = true;
    setLoading(true);
    setData([]);
    setActiveStage(null);
    getBidHistoryArchive({ tenderId, requisitionId })
      .then((res) => {
        if (!mounted) return;
        const raw = res?.data;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
        setData(list);
      })
      .catch(() => {
        if (mounted) setData([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [open, tenderId, requisitionId]);

  // Group bids by stage, merge all priceHistory entries, sort by createdAt
  const groupedStages = useMemo(() => {
    if (!data.length) return [];

    const grouped = {};
    data.forEach((bid) => {
      const stageMap = { CB: "IPO", AUCTION: "RA" };
      const stageLabel = stageMap[bid.bidStage] || bid.bidStage;
      if (!grouped[stageLabel]) grouped[stageLabel] = [];
      if (bid.priceHistory?.length > 0) {
        bid.priceHistory.forEach((entry) =>
          grouped[stageLabel].push({
            ...entry,
            bidderEntityName: bid.bidderEntityName ?? "-",
            winnerBidStatus: bid.winnerBidStatus ?? null,
          }),
        );
      } else if (bid.bidStage === "AUCTION" && bid.bidStatus !== "REJECTED") {
        // No price revisions for this AUCTION bid — fall back to the bid's
        // own values so the RA stage still shows the placed bid.
        // Skip rejected bids; they should not appear in the RA history.
        grouped[stageLabel].push({
          id: bid.connectId,
          bidPrice: bid.bidPrice,
          bidQuantityMw: bid.bidQuantityMw,
          createdAt: bid.lastUpdatedAt ?? bid.placedAt,
          bidderEntityName: bid.bidderEntityName ?? "-",
          winnerBidStatus: bid.winnerBidStatus ?? null,
          isFinal: true,
        });
      }
    });

    Object.keys(grouped).forEach((stage) => {
      grouped[stage].sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      );
    });

    const stageStatus = {};
    data.forEach((bid) => {
      const stageMap = { CB: "IPO", AUCTION: "RA" };
      const stageLabel = stageMap[bid.bidStage] || bid.bidStage;
      if (!stageStatus[stageLabel] && bid.bidStatus) {
        stageStatus[stageLabel] = bid.bidStatus;
      }
    });

    const ordered = STAGE_ORDER.filter((s) => grouped[s]).map((s) => ({
      stage: s,
      entries: grouped[s],
      status: stageStatus[s],
    }));
    Object.keys(grouped).forEach((s) => {
      if (!STAGE_ORDER.includes(s))
        ordered.push({ stage: s, entries: grouped[s], status: stageStatus[s] });
    });

    return ordered;
  }, [data]);

  // Fall back to a default RA tab with an empty table when there is no
  // bid history, so the layout stays consistent instead of showing a bare message.
  const visibleStages = useMemo(() => {
    const computedStages =
      role?.toLowerCase() === "consumer"
        ? groupedStages.filter((g) => g.stage === "RA")
        : groupedStages;
    return computedStages.length > 0
      ? computedStages
      : [{ stage: "RA", entries: [], status: null }];
  }, [groupedStages, role]);

  // Default to RA stage if available, otherwise first available stage
  useEffect(() => {
    if (visibleStages.length > 0 && !activeStage) {
      const raStage = visibleStages.find((g) => g.stage === "RA");
      setActiveStage(raStage ? "RA" : visibleStages[0].stage);
    }
  }, [visibleStages, activeStage]);

  const activeEntries = useMemo(() => {
    return visibleStages.find((g) => g.stage === activeStage)?.entries ?? [];
  }, [visibleStages, activeStage]);

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={`popupModal ${Styles.modalContainer}`}>
        {/* Header */}
        <div className={`display_flex_row modalHeading`}>
          <div className="headingText">Bid History</div>
          <Close
            style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>

        {/* Stage toggle tabs */}
        {!loading && visibleStages.length > 0 && (
          <div className={Styles.tabRow}>
            <div className={Styles.tabBar}>
              {visibleStages.map(({ stage }) => (
                <button
                  key={stage}
                  className={`${Styles.tabBtn} ${activeStage === stage ? Styles.tabBtnActive : ""}`}
                  onClick={() => setActiveStage(stage)}
                >
                  {stage}
                </button>
              ))}
            </div>
            {/* {(() => {
              const activeStatus = visibleStages.find(
                (g) => g.stage === activeStage,
              )?.status;

              return activeStatus && activeStage !== "RA" ? (
                <span
                  className={`${Styles.statusBadge} ${
                    Styles[`status_${activeStatus.toLowerCase()}`] || ""
                  }`}
                >
                  {activeStatus.replace(/_/g, " ")}
                </span>
              ) : null;
            })()} */}
          </div>
        )}

        {/* Body */}
        <div className={Styles.modalBody}>
          {loading && (
            <div className={Styles.skeletonWrap}>
              <div className={Styles.skeletonHeader}>
                {[8, 22, 22, 28, 12, 8].map((w, i) => (
                  <div
                    key={i}
                    className={Styles.skeletonCell}
                    style={{ width: `${w}%` }}
                  />
                ))}
              </div>
              {[1, 2, 3].map((r) => (
                <div key={r} className={Styles.skeletonRow}>
                  {[8, 22, 22, 28, 12, 8].map((w, i) => (
                    <div
                      key={i}
                      className={Styles.skeletonCell}
                      style={{ width: `${w}%`, animationDelay: `${r * 0.1}s` }}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
          {!loading && activeStage && (
            <StageSection
              entries={activeEntries}
              showEntityName={showEntityName}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default RABidHistory;
